import { StoreImageEnum } from '@systemeio/digital-store-shared'
import { LanguageCodeEnum } from '@systemeio/ui'
import { defaultDigitalProductErrors } from 'modules/digital-product/constants/default-values'
import { useDigitalProduct } from 'modules/digital-product/hooks/use-digital-product'
import {
  ResourceFileType,
  useResourceFiles,
} from 'modules/digital-product/hooks/use-resource-files'
import {
  DigitalProductChangeInterface,
  DigitalProductErrorInterface,
} from 'modules/digital-product/types/digital-product-interface'
import ItemActions from 'modules/stores/store/components/item-actions'
import ItemTabs, { ItemTabType } from 'modules/stores/store/components/item-tabs'
import ThankYouTab from 'modules/stores/store/components/tabs/thank-you-tab'
import { STORE_ITEM_CLASS_NAME } from 'modules/stores/store/constants/store-item-class-name'
import Thumbnail from 'modules/stores/store/content/product/components/thumbnail'
import Checkout from 'modules/stores/store/content/product/digital-product/components/checkout'
import { DigitalDownload } from 'modules/stores/store/content/product/digital-product/product-download/components/digital-download'
import { defaultErrors } from 'modules/stores/store/content/product/digital-product/product-download/constants/default-errors'
import { defaultValues } from 'modules/stores/store/content/product/digital-product/product-download/constants/default-values'
import {
  ProductDownloadTabEnum,
  tabLocoKeys,
} from 'modules/stores/store/content/product/digital-product/product-download/enum/product-download-tab-enum'
import { useErrorMessageInTabs } from 'modules/stores/store/content/product/digital-product/product-download/hooks/use-error-message-in-tabs'
import { getNextTab } from 'modules/stores/store/content/product/digital-product/product-download/utils/get-next-tab-product-download'
import {
  ProductDownloadItemEditType,
  ProductDownloadItemErrorsType,
  ProductDownloadItemViewType,
} from 'modules/stores/store/content/product/types/product-interface'
import { useStoreEditItemContext } from 'modules/stores/store/context/store-item-edit-context'
import { useStore } from 'modules/stores/store/hooks/use-store'
import { useStoreContactFields } from 'modules/stores/store/hooks/use-store-contact-fields'
import { StoreId } from 'modules/stores/types/stores-interface'
import { FC, useEffect, useState } from 'react'
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { deepCompare } from 'shared/utils/deepCompare'
import { twMerge } from 'tailwind-merge'
import { useSaveItemProduct } from '../hooks/use-save-item-product'
import { digitalProductInitFields } from '../constants/mandatory-fields'

interface Props {
  storeId: StoreId
  item?: ProductDownloadItemViewType
  languageCode: LanguageCodeEnum
}

const Edit: FC<Props> = ({ storeId, item, languageCode }) => {
  const { t } = useLocoTranslation()

  const [tab, setTab] = useState<ProductDownloadTabEnum>(ProductDownloadTabEnum.thumbnail)

  const { data: storeView } = useStore(storeId)

  const defaultValuesWithLoco: ProductDownloadItemEditType = {
    ...defaultValues,
    draft: false,
    title: t('dashboard.store.product.download.title_default', { lng: languageCode }),
    subTitle: t('dashboard.store.product.download.subtitle_default', { lng: languageCode }),
    buttonTitle: t('dashboard.store.product.download.button_default', { lng: languageCode }),
    checkoutPage: {
      ...defaultValues.checkoutPage,
      title: t('dashboard.store.product.checkout.title_default', { lng: languageCode }),
      description: t('dashboard.store.product.checkout.subtitle_default_tip-tap', {
        lng: languageCode,
      }),
      buttonTitle: t('dashboard.store.product.checkout.button_default', { lng: languageCode }),
    },
    thankYouPage: {
      title: t('dashboard.store.product.download.thank_you.title_default', { lng: languageCode }),
      text: t('dashboard.store.product.download.thank_you.subtitle_default', { lng: languageCode }),
    },
  }

  const defaultProductValuesWithLoco: DigitalProductChangeInterface = {
    name: t('dashboard.store.product.download.title_default', { lng: languageCode }),
    resources: [],
    pricePlans: [],
    confirmationEmail: null,
  }

  const [tempState, setTempState] = useState<ProductDownloadItemEditType>(
    item || defaultValuesWithLoco,
  )

  const { data: digitalProduct } = useDigitalProduct(tempState.checkoutPage.digitalProductId)

  const [tempProduct, setTempProduct] = useState<DigitalProductChangeInterface>(
    digitalProduct ?? defaultProductValuesWithLoco,
  )

  const [isDataSaved, setIsDataSaved] = useState<boolean>(true)

  useEffect(() => {
    if (digitalProduct) {
      setTempProduct(digitalProduct)
    }
  }, [digitalProduct])

  const [tempFiles, setTempFiles] = useState<ResourceFileType[]>([])
  const { data: resourceFiles, ...resourceFetcher } = useResourceFiles(tempProduct.id ?? undefined)
  const isResourceFilesLoading = resourceFetcher.isLoading || resourceFetcher.isValidating

  useEffect(() => {
    resourceFiles && setTempFiles(resourceFiles)
  }, [resourceFiles])

  const { errors: initialErrors, setErrors: setEditItemErrors } = useStoreEditItemContext()
  const [errors, setErrors] = useState<ProductDownloadItemErrorsType>({
    ...defaultErrors,
    ...initialErrors,
  })
  const [productErrors, setProductErrors] = useState<DigitalProductErrorInterface>(
    defaultDigitalProductErrors,
  )

  useEffect(() => {
    return () => {
      setEditItemErrors({})
    }
  }, [])

  useStoreContactFields({
    savedFields: tempState.checkoutPage.fields,
    initFields: digitalProductInitFields,
    onChange: fields =>
      setTempState(prev => ({ ...prev, checkoutPage: { ...prev.checkoutPage, fields } })),
  })

  const { onPublish, isFetchingPublish, isFetchingDraft, onSaveDraft } = useSaveItemProduct({
    tempState,
    tempProduct,
    setTempProduct,
    setTempState,
    storeId,
    setErrors,
    setProductErrors,
    defaultErrors,
  })
  const isLastTab = tab === ProductDownloadTabEnum.thank_you

  const isCreatingProcess = !item

  const { getTabsWithErrors } = useErrorMessageInTabs()

  const errorsByTab = getTabsWithErrors({
    errors,
    digitalProductErrors: productErrors,
  })

  const isNew = !item

  const inReadyToSave = isNew ? true : !!digitalProduct

  const tabs: ItemTabType<ProductDownloadTabEnum>[] = [
    {
      key: ProductDownloadTabEnum.thumbnail,
      caption: t(tabLocoKeys[ProductDownloadTabEnum.thumbnail]),
      hasError: errorsByTab[ProductDownloadTabEnum.thumbnail],
    },
    {
      key: ProductDownloadTabEnum.digital_download,
      caption: t(tabLocoKeys[ProductDownloadTabEnum.digital_download]),
      hasError: errorsByTab[ProductDownloadTabEnum.digital_download],
    },
    {
      key: ProductDownloadTabEnum.checkout,
      caption: t(tabLocoKeys[ProductDownloadTabEnum.checkout]),
      hasError: errorsByTab[ProductDownloadTabEnum.checkout],
    },
    {
      key: ProductDownloadTabEnum.thank_you,
      caption: t(tabLocoKeys[ProductDownloadTabEnum.thank_you]),
      hasError: errorsByTab[ProductDownloadTabEnum.thank_you],
    },
  ]

  useEffect(() => {
    if (item || resourceFiles || digitalProduct) {
      setIsDataSaved(
        deepCompare(
          { item: tempState, digitalProduct: tempProduct, resourceFiles: tempFiles },
          {
            item: item ?? defaultValuesWithLoco,
            digitalProduct: digitalProduct ?? defaultProductValuesWithLoco,
            resourceFiles: resourceFiles ?? [],
          },
        ),
      )
    } else {
      setIsDataSaved(
        deepCompare(
          {
            item: tempState,
            digitalProduct: tempProduct,
            resourceFiles: tempFiles,
          },
          {
            item: {
              ...defaultValuesWithLoco,
              checkoutPage: {
                ...defaultValuesWithLoco.checkoutPage,
                fields: tempState.checkoutPage.fields,
                description: tempState.checkoutPage.description,
              },
              style: tempState.style,
            },
            digitalProduct: defaultProductValuesWithLoco,
            resourceFiles: [],
          },
        ),
      )
    }
  }, [tempState, tempProduct, tempFiles])

  return (
    <div className={twMerge(STORE_ITEM_CLASS_NAME, 'flex flex-col gap-5')}>
      <ItemTabs tabs={tabs} currentTab={tab} setTab={setTab} />
      {tab === ProductDownloadTabEnum.thumbnail && (
        <Thumbnail
          imageType={StoreImageEnum.product_download_thumbnail}
          tempState={tempState}
          setTempState={setTempState}
          errors={errors}
        />
      )}
      {tab === ProductDownloadTabEnum.checkout && (
        <Checkout
          tempState={tempState}
          setTempState={setTempState}
          tempProduct={tempProduct}
          setTempProduct={setTempProduct}
          errors={errors}
          pricePlanErrors={productErrors.pricePlans}
          currency={storeView?.currency}
          paymentMethods={storeView?.paymentMethods}
          imageType={StoreImageEnum.product_download_cover}
        />
      )}
      {tab === ProductDownloadTabEnum.digital_download && (
        <DigitalDownload
          tempProduct={tempProduct}
          setTempProduct={setTempProduct}
          tempFiles={tempFiles}
          setTempFiles={setTempFiles}
          isResourceFilesLoading={isResourceFilesLoading}
          productItemErrors={errors}
          digitalProductErrors={productErrors}
        />
      )}
      {tab === ProductDownloadTabEnum.thank_you && (
        <ThankYouTab
          thankYouPage={tempState.thankYouPage}
          onChange={thankYouPage => setTempState(prev => ({ ...prev, thankYouPage }))}
          errors={errors}
        />
      )}
      <ItemActions
        publish={{
          disabled: !inReadyToSave,
          onClick: isLastTab || !isCreatingProcess ? onPublish : undefined,
          isFetching: isFetchingPublish,
        }}
        saveDraft={{ disabled: !inReadyToSave, onClick: onSaveDraft, isFetching: isFetchingDraft }}
        next={{
          onClick: isCreatingProcess && !isLastTab ? () => setTab(getNextTab) : undefined,
        }}
      />
      <ConfirmationModalBeforeExit
        confirmationText={t('dashboard.store.confirmation_message_before_exit')}
        hideModal={isDataSaved || isFetchingDraft || isFetchingPublish}
      />
    </div>
  )
}

export default Edit
